import React, { useState, useRef, useEffect } from 'react';
import {
   Box,
   Button,
   Container,
   TextField,
   Typography,
   Paper,
   Grid,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   FormHelperText,
   CircularProgress,
   Alert,
   Divider,
   Stack,
   FormControlLabel,
   Switch,
   SelectChangeEvent,
   IconButton,
   Tooltip,
   Chip,
   InputAdornment,
} from '@mui/material';
import {
   Add as AddIcon,
   Photo as PhotoIcon,
   Event as EventIcon,
   Place as PlaceIcon,
   Send as SendIcon,
   Info as InfoIcon,
   AutoAwesome as AutoAwesomeIcon,
   Videocam as VideocamIcon,
   Computer as ComputerIcon,
   Mic as MicIcon,
   RecordVoiceOver as RecordVoiceOverIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvents } from '../../contexts/EventContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { EventFormData, EventType } from '../../types/Event';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CoverPhotoUpload from '../../components/EventForms/CoverPhotoUpload';
import { enhanceEventDescription } from '../../services/aiService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import GooglePlacesSearch from '../../components/common/GooglePlacesSearch';
import { useUser } from '../../contexts/UserContext';

// Event type options
const eventTypes: { value: EventType; label: string }[] = [
   { value: 'social', label: 'Social Gathering' },
   { value: 'family', label: 'Family Event' },
   { value: 'cultural', label: 'Cultural Event' },
   { value: 'sports', label: 'Sports Event' },
   { value: 'business', label: 'Business Event' },
   { value: 'other', label: 'Other' },
];

// Add Quill modules configuration
const quillModules = {
   toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      [{ color: [] }],
      ['blockquote', 'link'],
      ['clean'],
   ],
   clipboard: {
      matchVisual: false,
   },
};

const quillFormats = [
   'header',
   'bold',
   'italic',
   'underline',
   'list',
   'bullet',
   'align',
   'color',
   'blockquote',
   'link',
];

const CreateEventPage: React.FC = () => {
   const navigate = useNavigate();
   const { id: organizationId } = useParams<{ id: string }>();
   const { createEvent, getEvent, updateEvent } = useEvents();
   const { organization } = useOrganization();
   const { eventId: eventIdParam } = useParams<{ eventId: string }>();
   const [isEditMode, setIsEditMode] = useState(!!eventIdParam);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const { currentUser } = useUser();

   // Form state
   const [formData, setFormData] = useState<EventFormData>({
      title: '',
      description: '',
      eventType: 'social',
      startDate: null,
      endDate: null,
      location: null,
      coverImage: null,
      isPublic: false,
      hasTickets: organizationId ? false : false,
      organizationId: organizationId || '',
      tags: [],
      isVirtual: false,
      virtualMeetingUrl: '',
      recordingEnabled: false,
      virtualEventProvider: '',
      virtualEventPassword: '',
      requireRegistration: true,
      maxParticipants: 40,
      enableParticipantSharing: true, // Default to enabled for better networking
   });

   // Location state for address input
   const [locationInput, setLocationInput] = useState('');

   // UI state
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [previewUrl, setPreviewUrl] = useState<string | null>(null);
   const [newTag, setNewTag] = useState(''); // Add state for new tag input
   const [creatingVirtualRoom, setCreatingVirtualRoom] = useState(false);

   // Cover photo handling
   const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
   const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);

   const [enhancing, setEnhancing] = useState(false);
   const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
   const [suggestedHighlights, setSuggestedHighlights] = useState<string[]>([]);

   useEffect(() => {
      // If we're in edit mode, fetch the event data
      if (eventIdParam) {
         const fetchEventData = async () => {
            try {
               setLoading(true);
               const eventData = await getEvent(eventIdParam);
               if (eventData) {
                  setFormData({
                     title: eventData.title || '',
                     description: eventData.description || '',
                     eventType: (eventData.eventType as EventType) || 'social',
                     startDate: eventData.startDate ? new Date(eventData.startDate) : null,
                     endDate: eventData.endDate ? new Date(eventData.endDate) : null,
                     location: typeof eventData.location === 'object' ? eventData.location : null,
                     coverImage: null, // We'll handle the image separately
                     isPublic: false, // All events are private
                     hasTickets: eventData.hasTickets || false,
                     organizationId: eventData.organizationId || organizationId || '',
                     tags: eventData.tags || [],
                     isVirtual: eventData.isVirtual || false,
                     virtualMeetingUrl: eventData.virtualMeetingUrl || '',
                     recordingEnabled: eventData.recordingEnabled || false,
                     virtualEventProvider: eventData.virtualEventProvider || '',
                     virtualEventPassword: eventData.virtualEventPassword || '',
                     requireRegistration: eventData.requireRegistration || true,
                     enableParticipantSharing:
                        eventData.enableParticipantSharing !== undefined
                           ? eventData.enableParticipantSharing
                           : true,
                  });

                  // Set location input for display
                  if (typeof eventData.location === 'object' && eventData.location?.name) {
                     setLocationInput(eventData.location.name);
                  } else if (eventData.isVirtual) {
                     // For virtual events without proper location object, set default
                     setLocationInput('Virtual Event');
                  }

                  // Set cover photo URL if it exists
                  if (eventData.coverImageUrl) {
                     setCoverPhotoUrl(eventData.coverImageUrl);
                  }
               }
            } catch (err) {
               console.error('Error fetching event data:', err);
               setError('Failed to load event data. Please try again.');
            } finally {
               setLoading(false);
            }
         };
         fetchEventData();
      }
   }, [eventIdParam, getEvent]);

   // Update handleInputChange to handle both regular inputs and Quill editor
   const handleInputChange = (
      e:
         | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
         | SelectChangeEvent<string>
         | string,
      name?: string
   ) => {
      if (typeof e === 'string' && name) {
         // Handle Quill editor input
         setFormData((prev) => ({ ...prev, [name]: e }));
      } else if (typeof e === 'object' && 'target' in e) {
         // Handle regular input or select input
         const { name, value } = e.target;
         setFormData((prev) => ({ ...prev, [name]: value }));
      }
   };

   const handleSelectChange = (e: SelectChangeEvent<EventType>) => {
      const { name, value } = e.target;
      if (name) {
         setFormData((prev) => ({ ...prev, [name]: value }));
      }
   };

   const handleDateChange = (date: Date | null, field: 'startDate' | 'endDate') => {
      setFormData((prev) => ({ ...prev, [field]: date }));
   };

   const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;

      if (name === 'isVirtual') {
         setFormData((prev) => ({
            ...prev,
            [name]: checked,
            // Auto-set location for virtual events
            location: checked
               ? { name: 'Virtual Event', address: 'Virtual Event', coordinates: [0, 0] }
               : null,
         }));

         // Update location input display
         if (checked) {
            setLocationInput('Virtual Event');
         } else {
            setLocationInput('');
         }
      } else {
         setFormData((prev) => ({ ...prev, [name]: checked }));
      }
   };

   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
         const file = e.target.files[0];
         setFormData((prev) => ({ ...prev, coverImage: file }));

         // Create a preview URL
         const reader = new FileReader();
         reader.onload = () => {
            setPreviewUrl(reader.result as string);
         };
         reader.readAsDataURL(file);
      }
   };

   const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocationInput(e.target.value);
   };

   const handleLocationSave = (location: {
      name: string;
      address: string;
      coordinates: [number, number];
   }) => {
      setFormData((prev) => ({
         ...prev,
         location: {
            name: location.name,
            address: location.address,
            coordinates: location.coordinates,
         },
      }));
   };

   // Tag management handlers
   const handleAddTag = () => {
      if (!newTag.trim()) return;

      // Don't add duplicate tags
      if (formData.tags?.includes(newTag.trim())) {
         setError('This tag already exists');
         return;
      }

      setFormData((prev) => ({
         ...prev,
         tags: [...(prev.tags || []), newTag.trim()],
      }));
      setNewTag('');
      setError(null);
   };

   const handleRemoveTag = (tagToRemove: string) => {
      setFormData((prev) => ({
         ...prev,
         tags: prev.tags?.filter((tag) => tag !== tagToRemove) || [],
      }));
   };

   const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         handleAddTag();
      }
   };

   const handleAddSuggestedTag = (tag: string) => {
      if (formData.tags?.includes(tag)) {
         setError('This tag already exists');
         return;
      }

      setFormData((prev) => ({
         ...prev,
         tags: [...(prev.tags || []), tag],
      }));
   };

   const handleCoverPhotoChange = (imageUrl: string | null, file: File | null) => {
      setCoverPhotoUrl(imageUrl);
      setCoverPhotoFile(file);
   };

   const handleEnhanceWithAI = async () => {
      if (!formData.title) {
         setError('Please enter a basic title first');
         return;
      }

      try {
         setEnhancing(true);
         setError(null);

         const enhancement = await enhanceEventDescription(
            formData.title,
            formData.description || 'A professional event', // Provide default if description is empty
            formData.eventType
         );

         // Update form with enhanced content
         setFormData((prev) => ({
            ...prev,
            title: enhancement.suggestedTitle || prev.title,
            description: enhancement.enhancedDescription,
         }));

         setSuggestedTags(enhancement.suggestedTags || []);
         setSuggestedHighlights(enhancement.suggestedHighlights || []);
      } catch (err) {
         console.error('Error enhancing description:', err);

         // Handle specific error cases
         if (err instanceof Error) {
            if (err.message.includes('API key not configured')) {
               setError(
                  'Unable to access AI service. Please ensure the environment is properly configured.'
               );
            } else if (err.message.includes('rate limit')) {
               setError(
                  'AI service is temporarily unavailable. Please try again in a few minutes.'
               );
            } else {
               setError('Failed to enhance description. Please try again.');
            }
         } else {
            setError('An unexpected error occurred. Please try again.');
         }
      } finally {
         setEnhancing(false);
      }
   };

   const validateForm = (): boolean => {
      if (!formData.title) {
         setError('Please enter a title for your event');
         return false;
      }

      if (!formData.startDate) {
         setError('Please select a start date for your event');
         return false;
      }

      if (!formData.location) {
         setError('Please enter a location for your event');
         return false;
      }

      // Virtual event provider validation removed since we automatically use Twilio

      if (formData.isVirtual && formData.maxParticipants && formData.maxParticipants < 1) {
         setError('Please enter a valid number of participants');
         return false;
      }

      setError(null);
      return true;
   };

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) return;

      try {
         setLoading(true);
         setError(null);

         let twilioRoomData = null;

         // Automatically create Twilio room for any virtual event
         if (formData.isVirtual) {
            setCreatingVirtualRoom(true);

            try {
               // Call twilio api to create a virtual room from backend server
               const { getAuthHeaders } = await import('../../utils/auth');
               const headers = await getAuthHeaders();

               const result = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/twilio/create-room`,
                  {
                     method: 'POST',
                     headers,
                     body: JSON.stringify({
                        roomName: formData.title,
                        roomType: 'group',
                        maxParticipants: formData.maxParticipants || 10,
                        recordParticipantsOnConnect: formData.recordingEnabled || false,
                     }),
                  }
               );

               const data = await result.json();

               if (!data.success) {
                  setError('Failed to create virtual room. Please try again.');
                  setLoading(false);
                  setCreatingVirtualRoom(false);
                  return;
               }

               twilioRoomData = data;
            } catch (twilioError) {
               console.error('Error creating Twilio room:', twilioError);
               setError('Failed to create virtual meeting room. Please try again.');
               setLoading(false);
               setCreatingVirtualRoom(false);
               return;
            }
         }

         const eventData: any = {
            ...formData,
            description: formData.description, // Clean the HTML content
            coverImage: coverPhotoFile,
            coverImageUrl: coverPhotoUrl,
            tags: formData.tags,
            owner: currentUser?.uid,
            // Include Twilio room data if virtual event
            ...(twilioRoomData && {
               virtualRoomSid: twilioRoomData.roomSid,
               virtualRoomName: twilioRoomData.roomName,
               virtualRoomUrl: twilioRoomData.url,
               virtualRoomStatus: twilioRoomData.status,
               virtualEventProvider: 'twilio', // Set provider to twilio when room is created
            }),
         };

         let resultEvent: any;
         if (isEditMode && eventIdParam) {
            resultEvent = await updateEvent(eventIdParam, eventData);
         } else {
            resultEvent = await createEvent(eventData);
         }

         setLoading(false);
         setError(null);
         setCreatingVirtualRoom(false);

         // // Handle both object with id or string event ID
         const resultEventId = typeof resultEvent === 'string' ? resultEvent : resultEvent?.id;
         if (resultEventId) {
            navigate(`/events/${resultEventId}`);
         }
      } catch (err: any) {
         console.error('Error creating event:', err);

         // Check for subscription required error
         if (err.message === 'SUBSCRIPTION_REQUIRED') {
            setError(
               'You need a subscription to create additional events. Please subscribe to continue.'
            );
            // Optionally redirect to subscription page
            setTimeout(() => {
               window.location.href = '/subscription';
            }, 3000);
         } else {
            setError(err.message || 'Failed to create event. Please try again.');
         }
         
         setLoading(false);
         setCreatingVirtualRoom(false);
      }
   };

   const handleSelectInputChange = (e: SelectChangeEvent<string>) => {
      handleInputChange(e, e.target.name);
   };

   return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
         <Container maxWidth='md' sx={{ mt: 3, pb: 4 }}>
            {/* Page header */}
            <Box sx={{ mb: 3 }}>
               <Typography variant='h5' component='h1' sx={{ fontWeight: 600 }}>
                  {organizationId ? 'Create organization event' : 'Create event'}
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                  {organizationId ? 'Add an event for your organization.' : 'Set up a new event and invite participants.'}
               </Typography>
            </Box>

            <Paper
               sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'divider',
               }}
            >
               {error && (
                  <Alert severity='error' sx={{ mb: 3 }}>
                     {error}
                  </Alert>
               )}

               <Box component='form' onSubmit={handleSubmit}>
                  {/* Section: Event basics */}
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
                     Event basics
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                     <Grid item xs={12}>
                        <TextField
                           fullWidth
                           label='Event title'
                           name='title'
                           value={formData.title}
                           onChange={handleInputChange}
                           placeholder='Enter your event title'
                           variant='outlined'
                           required
                        />
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                           <InputLabel id='event-type-label'>Event type</InputLabel>
                           <Select
                              labelId='event-type-label'
                              name='eventType'
                              value={formData.eventType}
                              onChange={handleSelectChange}
                              label='Event type'
                           >
                              {eventTypes.map((type) => (
                                 <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <FormControlLabel
                           control={
                              <Switch
                                 checked={formData.isPublic}
                                 onChange={handleSwitchChange}
                                 name='isPublic'
                                 color='primary'
                              />
                           }
                           label='Make this event public'
                        />
                        <FormHelperText>Visible to anyone; enables sharing.</FormHelperText>
                     </Grid>
                     <Grid item xs={12}>
                        <CoverPhotoUpload
                           initialImageUrl={coverPhotoUrl}
                           onChange={(imageUrl, file) => {
                              if (imageUrl) {
                                 setCoverPhotoUrl(imageUrl);
                                 setFormData((prev) => ({ ...prev, coverImage: null }));
                              } else if (file) {
                                 setCoverPhotoFile(file);
                                 setFormData((prev) => ({ ...prev, coverImage: file }));
                              } else {
                                 setCoverPhotoUrl(null);
                                 setCoverPhotoFile(null);
                                 setFormData((prev) => ({ ...prev, coverImage: null }));
                              }
                           }}
                           entityId={eventIdParam || `new-event-${Date.now()}`}
                           eventTitle={formData.title}
                           eventDescription={formData.description}
                           eventType={formData.eventType}
                        />
                     </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Section: When and where */}
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
                     When and where
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                     {/* Start date */}
                     <Grid item xs={12} sm={6}>
                        <DateTimePicker
                           label='Start Date & Time'
                           value={formData.startDate}
                           onChange={(date) => handleDateChange(date as Date | null, 'startDate')}
                           renderInput={(params) => <TextField {...params} fullWidth required />}
                        />
                     </Grid>

                     {/* End date */}
                     <Grid item xs={12} sm={6}>
                        <DateTimePicker
                           label='End Date & Time (Optional)'
                           value={formData.endDate}
                           onChange={(date) => handleDateChange(date as Date | null, 'endDate')}
                           renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                     </Grid>

                     {/* Location */}
                     <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                           <Typography
                              variant='subtitle2'
                              gutterBottom
                              sx={{ fontWeight: 500, mb: 1 }}
                           >
                              Event Location
                           </Typography>
                           <Stack direction='row' spacing={2}>
                              <GooglePlacesSearch
                                 //  sx={{ mb: 2 }}
                                 value={formData.location?.name || ''}
                                 searchType='places'
                                 label='Location'
                                 fullWidth
                                 disabled={formData.isVirtual}
                                 onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                                    handleLocationSave({
                                       name: place_name,
                                       address: locationInput,
                                       coordinates: [lat, lng],
                                    });
                                 }}
                              />
                              {/* <TextField
                                fullWidth
                                label='Address'
                                value={locationInput}
                                onChange={handleLocationInputChange}
                                placeholder='Enter event location'
                                required={!formData.location}
                             /> */}
                              {/* <Button
                      variant="outlined"
                      onClick={handleLocationSave}
                      startIcon={<PlaceIcon />}
                      disabled={!locationInput}
                    >
                      Set Location
                    </Button> */}
                           </Stack>
                        </Box>

                        {formData.location && (
                           <Alert severity='success' sx={{ mb: 2 }}>
                              Location: {formData.location.name}
                           </Alert>
                        )}
                     </Grid>

                     {/* Event format: Virtual */}
                     <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                           <Typography variant='subtitle2' gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                              Event format
                           </Typography>
                           <Grid container spacing={2} alignItems='center'>
                              <Grid item xs={12} sm={6}>
                                 <FormControlLabel
                                    control={
                                       <Switch
                                          checked={formData.isVirtual}
                                          onChange={handleSwitchChange}
                                          name='isVirtual'
                                          color='primary'
                                       />
                                    }
                                    label={
                                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <VideocamIcon sx={{ mr: 1 }} />
                                          <Typography>Virtual event</Typography>
                                       </Box>
                                    }
                                 />
                                 <FormHelperText>Add an online meeting for this event.</FormHelperText>
                              </Grid>

                              {!formData.isVirtual && (
                                 <Grid item xs={12} sm={6}>
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{ fontStyle: 'italic' }}
                                    >
                                       This is an in-person event only.
                                    </Typography>
                                 </Grid>
                              )}
                           </Grid>
                        </Box>

                        {formData.isVirtual && (
                           <Paper
                              variant='outlined'
                              sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: 'background.paper' }}
                           >
                              <Typography
                                 variant='subtitle2'
                                 gutterBottom
                                 sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                 <ComputerIcon sx={{ mr: 1, color: 'primary.main' }} />
                                 Virtual Event Details
                              </Typography>

                              <Grid container spacing={2}>
                                 <Grid item xs={12} md={12}>
                                    <FormControl fullWidth margin='normal'>
                                       <TextField
                                          label='Max-participant'
                                          name='maxParticipants'
                                          value={formData.maxParticipants}
                                          onChange={handleInputChange}
                                          type='number'
                                       />
                                    </FormControl>
                                    {/* <FormControl fullWidth margin='normal'>
                                       <InputLabel id='virtual-provider-label'>Platform</InputLabel>
                                       <Select
                                          labelId='virtual-provider-label'
                                          name='virtualEventProvider'
                                          value={formData.virtualEventProvider || 'twilio'}
                                          onChange={handleSelectInputChange}
                                          label='Platform'
                                       >
                                          <MenuItem value='twilio'>
                                             Twilio Video (Built-in)
                                          </MenuItem>
                                          <MenuItem value='zoom'>Zoom (External)</MenuItem>
                                          <MenuItem value='teams'>
                                             Microsoft Teams (External)
                                          </MenuItem>
                                          <MenuItem value='google-meet'>
                                             Google Meet (External)
                                          </MenuItem>
                                          <MenuItem value='custom'>Custom/Other</MenuItem>
                                       </Select>
                                       <FormHelperText>
                                          Built-in Twilio video is enabled by default for seamless
                                          integration
                                       </FormHelperText>
                                    </FormControl> */}
                                 </Grid>

                                 <Grid item xs={12} md={12}>
                                    <FormControlLabel
                                       control={
                                          <Switch
                                             checked={formData.recordingEnabled}
                                             onChange={handleSwitchChange}
                                             name='recordingEnabled'
                                             color='primary'
                                          />
                                       }
                                       label={
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                             <RecordVoiceOverIcon sx={{ mr: 1 }} />
                                             <Typography>Record Event</Typography>
                                          </Box>
                                       }
                                    />
                                    <FormHelperText>Recording available to participants after the event.</FormHelperText>
                                 </Grid>

                                 <Grid item xs={12}>
                                    <FormControlLabel
                                       control={
                                          <Switch
                                             checked={formData.requireRegistration}
                                             onChange={handleSwitchChange}
                                             name='requireRegistration'
                                             color='primary'
                                          />
                                       }
                                       label='Require participant registration'
                                    />
                                    <FormHelperText>Only registered participants can access the virtual event.</FormHelperText>
                                 </Grid>
                              </Grid>

                              <Alert severity='info' sx={{ mt: 2 }}>
                                 <Typography variant='body2'>
                                    A secure video room is created automatically for this event. Only registered participants will have access.
                                 </Typography>
                              </Alert>
                           </Paper>
                        )}
                     </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Section: Description */}
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
                     Event description
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                     <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                           <Typography variant='subtitle2' color='text.secondary'>
                              Add a title above, then generate a template with AI if you like.
                           </Typography>
                           <Button
                              size='small'
                              variant='outlined'
                              onClick={handleEnhanceWithAI}
                              disabled={enhancing || !formData.title}
                              startIcon={
                                 enhancing ? <CircularProgress size={18} color='inherit' /> : <AutoAwesomeIcon />
                              }
                           >
                              {enhancing ? 'Generating…' : 'Generate description with AI'}
                           </Button>
                        </Box>
                        <Box
                           sx={{
                              '.ql-container': {
                                 minHeight: '300px',
                                 fontSize: '1rem',
                              },
                              '.ql-editor': {
                                 minHeight: '300px',
                              },
                              '.ql-editor h1': {
                                 fontSize: '1.5rem',
                                 fontWeight: 'bold',
                                 marginBottom: '1rem',
                              },
                              '.ql-editor h2': {
                                 fontSize: '1.25rem',
                                 fontWeight: 'bold',
                                 marginBottom: '0.75rem',
                              },
                              '.ql-editor ul, .ql-editor ol': {
                                 paddingLeft: '2rem',
                                 marginBottom: '1rem',
                              },
                              '.ql-editor li': {
                                 marginBottom: '0.5rem',
                              },
                              '.ql-editor p': {
                                 marginBottom: '1rem',
                                 lineHeight: '1.6',
                              },
                           }}
                        >
                           <ReactQuill
                              value={formData.description}
                              onChange={(content) => handleInputChange(content, 'description')}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder='Create an engaging description that sells your event...'
                           />
                        </Box>

                        {suggestedHighlights.length > 0 && (
                           <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                              <Typography
                                 variant='subtitle2'
                                 gutterBottom
                                 sx={{ color: 'success.contrastText', fontWeight: 'bold' }}
                              >
                                 ✨ KEY HIGHLIGHTS
                              </Typography>
                              <ul style={{ color: 'success.contrastText', marginLeft: '1rem' }}>
                                 {suggestedHighlights.map((highlight, index) => (
                                    <li key={index} style={{ marginBottom: '0.5rem' }}>
                                       {highlight}
                                    </li>
                                 ))}
                              </ul>
                           </Box>
                        )}

                        {suggestedTags.length > 0 && (
                           <Box sx={{ mt: 2 }}>
                              <Typography
                                 variant='subtitle2'
                                 gutterBottom
                                 sx={{ fontWeight: 'bold' }}
                              >
                                 🏷️ SUGGESTED TAGS
                              </Typography>
                              <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                                 {suggestedTags.map((tag, index) => (
                                    <Chip
                                       key={index}
                                       label={tag}
                                       size='small'
                                       sx={{
                                          bgcolor: 'primary.light',
                                          color: 'primary.contrastText',
                                          '&:hover': {
                                             bgcolor: 'primary.main',
                                          },
                                       }}
                                    />
                                 ))}
                              </Stack>
                           </Box>
                        )}
                     </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Section: Optional details */}
                  <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
                     Optional details
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                     <Grid item xs={12}>
                        <Typography variant='subtitle2' gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                           Event tags
                        </Typography>
                        <Typography variant='body2' color='text.secondary' gutterBottom>
                           Add tags to help people find your event. Press Enter or click Add to save each tag.
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                           <Grid container spacing={2} alignItems='center'>
                              <Grid item xs>
                                 <TextField
                                    fullWidth
                                    size='small'
                                    label='Add tag'
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder='e.g., networking, workshop, tech'
                                    InputProps={{
                                       endAdornment: (
                                          <InputAdornment position='end'>
                                             <Button
                                                onClick={handleAddTag}
                                                disabled={!newTag.trim()}
                                                size='small'
                                             >
                                                Add
                                             </Button>
                                          </InputAdornment>
                                       ),
                                    }}
                                 />
                              </Grid>
                           </Grid>
                        </Box>

                        {/* Display current tags */}
                        {formData.tags && formData.tags.length > 0 && (
                           <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                              {formData.tags.map((tag, index) => (
                                 <Chip
                                    key={index}
                                    label={tag}
                                    onDelete={() => handleRemoveTag(tag)}
                                    color='primary'
                                    variant='outlined'
                                    size='small'
                                 />
                              ))}
                           </Box>
                        )}

                        {/* Display suggested tags if available */}
                        {suggestedTags.length > 0 && (
                           <Box sx={{ mt: 2 }}>
                              <Typography variant='subtitle2' gutterBottom>
                                 Suggested Tags
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                 {suggestedTags
                                    .filter((tag) => !formData.tags?.includes(tag))
                                    .map((tag, index) => (
                                       <Chip
                                          key={index}
                                          label={tag}
                                          onClick={() => handleAddSuggestedTag(tag)}
                                          color='primary'
                                          variant='outlined'
                                          size='small'
                                          sx={{ cursor: 'pointer' }}
                                       />
                                    ))}
                              </Box>
                           </Box>
                        )}
                     </Grid>

                     <Grid item xs={12}>
                        <Typography variant='subtitle2' gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                           Participants
                        </Typography>
                        <FormControlLabel
                           control={
                              <Switch
                                 checked={formData.enableParticipantSharing || false}
                                 onChange={(e) =>
                                    setFormData((prev) => ({
                                       ...prev,
                                       enableParticipantSharing: e.target.checked,
                                    }))
                                 }
                                 color='primary'
                              />
                           }
                           label='Enable participant collaboration'
                        />
                        <FormHelperText>
                           When enabled, invited participants can see each other and add others to their network. When disabled, only owners and editors see the full list.
                        </FormHelperText>
                     </Grid>

                     {/* Enable tickets toggle - only for org events */}
                     {organizationId && (
                        <Grid item xs={12}>
                           <FormControlLabel
                              control={
                                 <Switch
                                    checked={formData.hasTickets}
                                    onChange={handleSwitchChange}
                                    name='hasTickets'
                                    color='primary'
                                 />
                              }
                              label='Enable ticket sales or registration'
                           />
                           <FormHelperText>Free tickets, paid tickets, or simple RSVP.</FormHelperText>
                        </Grid>
                     )}
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                     <Button variant='outlined' onClick={() => navigate(-1)} disabled={loading}>
                        Cancel
                     </Button>
                     <Button
                        type='submit'
                        variant='contained'
                        color='primary'
                        size='large'
                        startIcon={
                           loading ? <CircularProgress size={20} color='inherit' /> : <SendIcon />
                        }
                        disabled={loading}
                        sx={{
                           background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                           boxShadow: '0 4px 15px rgba(1, 148, 154, 0.3)',
                           '&:hover': {
                              background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                              boxShadow: '0 6px 20px rgba(1, 148, 154, 0.4)',
                           },
                        }}
                     >
                        {creatingVirtualRoom
                           ? 'Creating virtual room…'
                           : loading
                             ? 'Creating event…'
                             : 'Create event'}
                     </Button>
                  </Box>
               </Box>
            </Paper>
         </Container>
      </LocalizationProvider>
   );
};

export default CreateEventPage;
